import * as TweenJS from "slotworks/utils/tween";
import * as particles from "pixi-particles";
import Contract from "slotworks/utils/contract";
import Layers from "slotworks/graphics/layers/layers";
import { CenterPivot } from "slotworks/graphics/pixi/group";
import { Easing } from "slotworks/utils/tween";
import currency from "slotworks/i18n/currency";
import { Sprite } from "pixi.js";
import graphicsManager from "slotworks/graphics/graphics-manager";
import { sequence } from "slotworks/utils/race-utils";
import deviceInfo from "slotworks/utils/device-info";
import Point from "slotworks/utils/geom/point";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import soundManager from "slotworks/sound/sound-manager";

export class RespinMeter {

    public static CONTAINER_NAME = "respin_meter_container";

    public static POPIN_TIME = 350;

    private value: PIXI.Text;
    private meter: PIXI.Sprite;
    private container: PIXI.Container;

    private jackpotValue: number = 0;

    private celebrationTween: TweenJS.Tween = null;
    private width: number = 0;

    public init(scene: string) {
        return new Contract(resolve => {
            Layers.Custom.get("RespinMeters").setScene(scene).then(() => {
                this.setUp();
                this.container.scale.set(0, 0);
                this.container.visible = false;

                if(!deviceInfo.isDesktop)
                    deviceInfo.onLayoutChange.add(this.onLayoutChange);
                resolve(null);
            });
        })
    }

    public setJackpotValue(value: number) {
        this.value.text = currency.format(value);
        this.jackpotValue = value;
    }

    public show() {
        return sequence([
            () => this.popIn(),
            () => this.moveUp()
        ])
    }

    public moveMeterToPlaquePortrait() {
        return new Contract<void>(resolve => {
            const prompt = Layers.Prompts.container.getChildAt(0);
            const promptPosition = Layers.Prompts.localToLocal(Layers.Custom.get("RespinMeters"), new Point(prompt.x, prompt.y));

            const distance = promptPosition.y - this.container.y;
            this.clean();
            new TweenJS.Tween(this.container)
            .to({x: promptPosition.x, y: this.container.y + distance/2}, 300)
            .onComplete(() => {

                Layers.Prompts.add(this.container);

                const promptPosition = Layers.Prompts.container.getChildAt(0).position;

                new TweenJS.Tween(this.container)
                .to({x: promptPosition.x, y: promptPosition.y}, 200)
                .delay(100)
                .onComplete(() => {
                    this.container.visible = false;
                    resolve(null);
                })
                .start()

                new TweenJS.Tween(this.container.scale)
                .to({x: 0, y: 0}, 200)
                .delay(100)
                .start();
            })
            .start();
            new TweenJS.Tween(this.container.scale)
            .to({x: 1.1, y: 1.1}, 400)
            .start();
        });
    }

    public moveMeterToPlaque() {
        soundManager.customEvent("jp_meter_move");
        if(canvasManager.orientation == Orientation.PORTRAIT) 
            return this.moveMeterToPlaquePortrait();
        return new Contract<void>(resolve => {
            const prompt = Layers.Prompts.container.getChildAt(0);
            const promptPosition = Layers.Prompts.localToLocal(Layers.Custom.get("RespinMeters"), new Point(prompt.x, prompt.y));
            const center = Layers.Custom.get("RespinMeters").globalToLocal(new Point(canvasManager.stageWidth/2, 0));

            const targetPositionX = center.x - (promptPosition.x - center.x) - this.width/2;
            
            new TweenJS.Tween(this.container)
            .to({x: targetPositionX, y: promptPosition.y}, 800)
            .onComplete(() => {
                resolve(null);
            })
            .start();

            
        });
    }

    public moveMeterToJackpot() {
        return new Contract<void>(resolve => {
            const winValue = Layers.CelebrationForeground.getBitmapText("value");
            const target = Layers.CelebrationForeground.localToLocal(Layers.Custom.get("RespinMeters"), new Point(winValue.x, winValue.y));
            this.clean();
            new TweenJS.Tween(this.container)
            .to({x: target.x, y: target.y}, 400)
            .onComplete(() => {
                this.container.visible = false;
                resolve(null);
            })
            .start();
        });
    }

    public startJackpotCelebration() {
        const animationPosition = Layers.Custom.get("RespinMeters").getPosition("starshine_win_loop");
        const animation = graphicsManager.createAnimation("starshine_win_loop");
        animation.anchor.set(0.5, 0.5);
        const scaleX = animationPosition.width/175;
        const scaleY = animationPosition.height/175;
        this.container.addChildAt(animation, 0);
        animation.position.set(this.container.pivot.x, this.container.pivot.y);
        animation.loop = true;

        animation.scale.set(0, 0);
        new TweenJS.Tween(animation.scale)
        .to({x: scaleX, y: scaleY}, 300)
        .onComplete(() => {
            animation.gotoAndPlay(0);
            this.startEndlessTween()
        })
        .start();
    }

    private startEndlessTween() {
        this.celebrationTween = new TweenJS.Tween(this.container.scale)
        .to({x: 1.1, y: 1.1}, 400)
        .repeat(Infinity)
        .yoyo(true)
        .start();
    }

    private onLayoutChange = () => {
        this.setUp();
        this.value.text = currency.format(this.jackpotValue);
    }

    private setUp() {
        this.value = Layers.Custom.get("RespinMeters").getText("value");
        this.meter = Layers.Custom.get("RespinMeters").getSprite("meter");
        this.width = this.meter.width;
        this.container = CenterPivot([this.meter, this.value], RespinMeter.CONTAINER_NAME);
        this.value.x -= 12;
        this.value.y -= 6;
    }

    private popIn():Contract<void> {
        return new Contract(resolve => {
            this.container.visible = true;
            const bottomPosition = Layers.Custom.get("RespinMeters").getPosition("bottom");
            this.container.position.set(bottomPosition.x + bottomPosition.width/2, bottomPosition.y + bottomPosition.height/2);
            new TweenJS.Tween(this.container.scale)
            .to({x: 1, y: 1}, RespinMeter.POPIN_TIME)
            .easing(Easing.Back.Out)
            .onComplete(resolve)
            .start();
        });
    }

    private moveUp() {
        return new Contract(resolve => {
            const topPosition = Layers.Custom.get("RespinMeters").getPosition("top");
            const topY = topPosition.y + topPosition.height/2;
            const diff = this.container.y - topY;
            new TweenJS.Tween(this.container)
            .to({x: topPosition.x + topPosition.width/2, y: topY}, diff * 1.9)
            .easing(Easing.Quintic.Out)
            .onComplete(resolve)
            .start();
        });
    }

    private clean() {
        if(this.celebrationTween) {
            this.celebrationTween.stop();
            TweenJS.remove(this.celebrationTween);
            this.celebrationTween = null;
        }
    }

    public destroy() {
        this.clean();
        if(!deviceInfo.isDesktop)
            deviceInfo.onLayoutChange.remove(this.onLayoutChange);
    }

}

export class PotTrain {

    public static DELAY = 120;
    
    private pots: Sprite[] = null;
    private targetHeight: number = 0;

    private type: string;

    private fromIndex: number;

    constructor(type: string, count: number) {
        this.type = type;
        this.targetHeight = count;
    } 

    public moveUp(alpha: number = 1, emitter: particles.Emitter = null, from: number = 0) {
        return new Contract(resolve => {
            const nextPot = (n:number) => {
                // this.pots[n].visible = true;
                // this.pots[n].alpha = alpha;
                this.fadeIn(this.pots[n], alpha);
                if(emitter) {
                    emitter.spawnPos.set(this.pots[n].x, this.pots[n].y - this.pots[n].height/2);
                    emitter.emit = true;
                }
                if(n + 1 < this.targetHeight) { 
                    setTimeout(() => nextPot(n + 1), PotTrain.DELAY);
                } else {
                    setTimeout(() => {
                        if(emitter) emitter.emit = false;
                        resolve(null);
                    }, PotTrain.DELAY);
                }
            }
            this.fromIndex = 0;
            setTimeout(() => nextPot(from), PotTrain.DELAY);
        });
    }

    public moveDown() {
        return new Contract(resolve => {
            const prevPot = (n:number) => {
                // this.pots[n].visible = false;
                this.fadeOut(this.pots[n]);
                if(n > this.fromIndex) { 
                    setTimeout(() => prevPot(n - 1), PotTrain.DELAY);
                } else {
                    setTimeout(resolve, PotTrain.DELAY);
                }
            }
            prevPot(this.targetHeight - 1);
        });
    }

    public destroy() {
        if(!deviceInfo.isDesktop) {
            deviceInfo.onLayoutChange.remove(this.onLayoutChange);
        }
    }

    public init() {
        this.pots = [];
        for(let i = 0; i < this.targetHeight; i++) {
            const pot = graphicsManager.createSprite(this.type + "pot");
            const target = Layers.Custom.get("Respin").getPosition(`pot_${this.type}_${i + 1}`);    
                
            Layers.Custom.get("Respin").add(pot);
            pot.anchor.set(0.5, 0.5);
            pot.width = target.width;
            pot.height = target.height;
            pot.position.set(target.x + target.width/2, target.y + target.height/2);
            pot.visible = false;
            this.pots[i] = pot;
        }
        if(!deviceInfo.isDesktop) {
            deviceInfo.onLayoutChange.add(this.onLayoutChange);
        }
    }

    private onLayoutChange = () => {
        for(let i = 0; i < this.targetHeight; i++) {
            const target = Layers.Custom.get("Respin").getPosition(`pot_${this.type}_${i + 1}`);
            this.pots[i].width = target.width;
            this.pots[i].height = target.height;
            this.pots[i].position.set(target.x + target.width/2, target.y + target.height/2);
        }
    }

    private fadeIn(sprite: Sprite, alpha: number = 1) {
        TweenJS.removeAll(sprite);
        sprite.visible = true;
        sprite.alpha = 0;
        new TweenJS.Tween(sprite)
        .to({alpha: alpha}, 200)
        .start();
    }

    private fadeOut(sprite: Sprite) {
        TweenJS.removeAll(sprite);
        new TweenJS.Tween(sprite)
        .to({alpha: 0}, 160)
        .onComplete(() => sprite.visible = false)
        .start();
    }
}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/respin/respin-elements.ts